import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { foursatAPI } from '../../lib/foursatApi';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Skeleton } from '../../components/ui/skeleton';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
    ArrowLeft,
    Satellite,
    Settings,
    RefreshCw,
    Download,
    Upload,
    Check,
    X,
    Loader2,
    Car,
    AlertCircle,
    Link2,
    Link2Off,
    Eye,
    EyeOff,
    Trash2,
} from 'lucide-react';

export default function FourSatIntegrationPage() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [testing, setTesting] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    
    const [config, setConfig] = useState(null);
    const [isConfigured, setIsConfigured] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    
    // Form state
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [apiHash, setApiHash] = useState('');
    const [showApiHash, setShowApiHash] = useState(false);
    const [syncInterval, setSyncInterval] = useState(24);
    const [autoSync, setAutoSync] = useState(false);
    const [syncDirection, setSyncDirection] = useState('bidirectional');
    
    // Sync results
    const [syncResult, setSyncResult] = useState(null);
    const [devices, setDevices] = useState([]);
    const [showDevices, setShowDevices] = useState(false);

    useEffect(() => {
        loadConfig();
    }, []);

    const loadConfig = async () => {
        try {
            setLoading(true);
            const res = await foursatAPI.getConfig();
            const data = res.data;
            
            setIsConfigured(data.configured);
            if (data.configured) {
                setEmail(data.email || '');
                setApiHash(data.api_hash || '');
                setSyncInterval(data.sync_interval_hours || 24);
                setAutoSync(data.auto_sync_enabled || false);
                setSyncDirection(data.sync_direction || 'bidirectional');
                setConfig(data);
            }
        } catch (err) {
            toast.error('Error al cargar configuración');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!email) {
            toast.error('Email es requerido');
            return;
        }
        
        if (!password && !apiHash && !isConfigured) {
            toast.error('Contraseña o API Hash son requeridos');
            return;
        }
        
        setSaving(true);
        try {
            const payload = {
                email,
                sync_interval_hours: syncInterval,
                auto_sync_enabled: autoSync,
                sync_direction: syncDirection
            };
            
            // Solo enviar password si tiene valor
            if (password && password.trim()) {
                payload.password = password.trim();
            }
            
            // Solo enviar api_hash si tiene valor
            if (apiHash && apiHash.trim()) {
                payload.api_hash = apiHash.trim();
            }
            
            await foursatAPI.saveConfig(payload);
            toast.success('Configuración guardada y conexión verificada');
            setPassword(''); // Limpiar password después de guardar
            loadConfig();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al guardar configuración');
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        setTesting(true);
        try {
            const res = await foursatAPI.testConnection();
            if (res.data.success) {
                toast.success(res.data.message || 'Conexión exitosa');
            } else {
                toast.error(res.data.message || 'Error de conexión');
            }
        } catch (err) {
            toast.error('Error al probar conexión');
        } finally {
            setTesting(false);
        }
    };

    const handleSyncImport = async () => {
        setSyncing(true);
        setSyncResult(null);
        try {
            // Iniciar sincronización en background
            await foursatAPI.syncImport();
            toast.info('Sincronización iniciada. Esto puede tomar unos segundos...');
            
            // Polling del estado cada 2 segundos
            const pollStatus = async () => {
                try {
                    const statusRes = await foursatAPI.getSyncStatus();
                    const status = statusRes.data;
                    
                    if (status.status === 'completed') {
                        setSyncResult(status.last_sync_result);
                        toast.success(`Importación completada: ${status.last_sync_result.imported} nuevos, ${status.last_sync_result.updated} actualizados`);
                        setSyncing(false);
                        loadConfig();
                    } else if (status.status === 'error') {
                        toast.error(`Error: ${status.last_sync_error}`);
                        setSyncing(false);
                    } else if (status.status === 'in_progress') {
                        // Seguir esperando
                        setTimeout(pollStatus, 2000);
                    } else {
                        setSyncing(false);
                    }
                } catch (err) {
                    setSyncing(false);
                    toast.error('Error al verificar estado');
                }
            };
            
            // Iniciar polling después de 2 segundos
            setTimeout(pollStatus, 2000);
        } catch (err) {
            setSyncing(false);
            toast.error('Error al iniciar sincronización');
        }
    };

    const handleSyncExport = async () => {
        setSyncing(true);
        setSyncResult(null);
        try {
            const res = await foursatAPI.syncExportAll();
            setSyncResult(res.data);
            toast.success(`Exportación completada: ${res.data.exported} vehículos actualizados en 4sat.cl`);
        } catch (err) {
            toast.error('Error en la exportación');
        } finally {
            setSyncing(false);
        }
    };

    const handleSyncFull = async () => {
        setSyncing(true);
        setSyncResult(null);
        try {
            const res = await foursatAPI.syncFull();
            setSyncResult(res.data);
            toast.success('Sincronización bidireccional completada');
        } catch (err) {
            toast.error('Error en la sincronización');
        } finally {
            setSyncing(false);
        }
    };

    const handleCapturaExcel = async () => {
        setDownloading(true);
        toast.info('Iniciando captura de datos de 4sat...');
        
        try {
            // Iniciar captura en background
            const res = await foursatAPI.iniciarCaptura();
            const capturaId = res.data.captura_id;
            
            toast.info('Captura iniciada. Descargando datos, esto puede tomar hasta 30 segundos...');
            
            // Polling del estado cada 3 segundos
            const pollStatus = async () => {
                try {
                    const statusRes = await foursatAPI.estadoCaptura(capturaId);
                    const status = statusRes.data;
                    
                    if (status.status === 'completed') {
                        // Descargar archivo
                        const downloadRes = await foursatAPI.descargarCaptura(capturaId);
                        
                        const blob = new Blob([downloadRes.data], { 
                            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
                        });
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', `captura_4sat_${new Date().toISOString().slice(0,10)}.xlsx`);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                        
                        toast.success(`Excel descargado: ${status.devices_count} dispositivos`);
                        setDownloading(false);
                    } else if (status.status === 'error') {
                        toast.error(`Error: ${status.error}`);
                        setDownloading(false);
                    } else if (status.status === 'in_progress') {
                        setTimeout(pollStatus, 3000);
                    } else {
                        setDownloading(false);
                    }
                } catch (err) {
                    setDownloading(false);
                    toast.error('Error al verificar estado de captura');
                }
            };
            
            // Iniciar polling después de 3 segundos
            setTimeout(pollStatus, 3000);
            
        } catch (err) {
            console.error('Error:', err);
            toast.error('Error al iniciar captura');
            setDownloading(false);
        }
    };

    const handleViewDevices = async () => {
        setShowDevices(true);
        try {
            const res = await foursatAPI.getDevices();
            setDevices(res.data.devices || []);
        } catch (err) {
            toast.error('Error al obtener dispositivos');
        }
    };

    const handleImportacionInteligente = async () => {
        setImporting(true);
        setImportResult(null);
        toast.info('Iniciando importación inteligente... esto puede tomar varios minutos');
        
        try {
            const res = await foursatAPI.iniciarImportacion();
            const importId = res.data.import_id;
            
            // Polling del estado cada 5 segundos
            const pollStatus = async () => {
                try {
                    const statusRes = await foursatAPI.estadoImportacion(importId);
                    const status = statusRes.data;
                    
                    if (status.status === 'completed') {
                        setImportResult(status.progress);
                        const p = status.progress;
                        toast.success(
                            `Importación completada: ${p.clientes_creados} clientes nuevos, ` +
                            `${p.vehiculos_creados} vehículos nuevos, ` +
                            `${p.dispositivos_instalados} dispositivos instalados, ` +
                            `${p.dispositivos_stock} a stock`
                        );
                        setImporting(false);
                    } else if (status.status === 'error') {
                        toast.error(`Error: ${status.error}`);
                        setImportResult(status.progress);
                        setImporting(false);
                    } else if (status.status === 'in_progress') {
                        // Mostrar progreso
                        const p = status.progress;
                        if (p.total_procesados > 0) {
                            toast.info(`Procesando... ${p.total_procesados}/${p.total_dispositivos} dispositivos`, {
                                id: 'import-progress'
                            });
                        }
                        setTimeout(pollStatus, 5000);
                    } else {
                        setImporting(false);
                    }
                } catch (err) {
                    setImporting(false);
                    toast.error('Error al verificar estado de importación');
                }
            };
            
            // Iniciar polling después de 5 segundos
            setTimeout(pollStatus, 5000);
            
        } catch (err) {
            console.error('Error:', err);
            toast.error('Error al iniciar importación inteligente');
            setImporting(false);
        }
    };

    const handleDelete = async () => {
        try {
            await foursatAPI.deleteConfig();
            toast.success('Configuración eliminada');
            setShowDeleteDialog(false);
            setIsConfigured(false);
            setEmail('');
            setPassword('');
            setConfig(null);
        } catch (err) {
            toast.error('Error al eliminar configuración');
        }
    };

    if (loading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <Satellite className="h-6 w-6" />
                            Integración 4sat.cl
                        </h1>
                        <p className="text-muted-foreground">
                            Sincroniza vehículos y dispositivos GPS
                        </p>
                    </div>
                </div>
                <Badge variant={isConfigured ? "default" : "secondary"} className="text-sm">
                    {isConfigured ? (
                        <><Link2 className="h-3 w-3 mr-1" /> Conectado</>
                    ) : (
                        <><Link2Off className="h-3 w-3 mr-1" /> No configurado</>
                    )}
                </Badge>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                {/* Configuración */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Settings className="h-5 w-5" />
                            Credenciales
                        </CardTitle>
                        <CardDescription>
                            Ingresa tus credenciales de acceso a 4sat.cl
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label>Email</Label>
                            <Input
                                type="email"
                                placeholder="usuario@ejemplo.cl"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="foursat-email"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Contraseña</Label>
                            <div className="relative">
                                <Input
                                    type={showPassword ? "text" : "password"}
                                    placeholder={isConfigured ? "••••••••" : "Ingresa tu contraseña"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    data-testid="foursat-password"
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="absolute right-0 top-0 h-full"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </Button>
                            </div>
                            {isConfigured && (
                                <p className="text-xs text-muted-foreground">
                                    Deja en blanco para mantener la contraseña actual
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label>API Hash (opcional)</Label>
                            <div className="relative">
                                <Input
                                    type={showApiHash ? "text" : "password"}
                                    placeholder={isConfigured && apiHash ? "••••••••••••••••" : "Ingresa el API Hash si lo tienes"}
                                    value={apiHash}
                                    onChange={(e) => setApiHash(e.target.value)}
                                    data-testid="foursat-api-hash"
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="absolute right-0 top-0 h-full"
                                    onClick={() => setShowApiHash(!showApiHash)}
                                >
                                    {showApiHash ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </Button>
                            </div>
                            <p className="text-xs text-muted-foreground">
                                Si proporcionas el API Hash, no es necesaria la contraseña
                            </p>
                        </div>

                        <div className="space-y-2">
                            <Label>Dirección de sincronización</Label>
                            <Select value={syncDirection} onValueChange={setSyncDirection}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="bidirectional">Bidireccional (recomendado)</SelectItem>
                                    <SelectItem value="import_only">Solo importar (4sat → Sistema)</SelectItem>
                                    <SelectItem value="export_only">Solo exportar (Sistema → 4sat)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="flex items-center justify-between pt-2">
                            <div>
                                <Label>Sincronización automática</Label>
                                <p className="text-xs text-muted-foreground">
                                    Sincronizar cada {syncInterval} horas
                                </p>
                            </div>
                            <Switch
                                checked={autoSync}
                                onCheckedChange={setAutoSync}
                            />
                        </div>

                        <div className="flex gap-2 pt-4">
                            <Button 
                                onClick={handleSave} 
                                disabled={saving}
                                className="flex-1"
                                data-testid="save-foursat-config"
                            >
                                {saving ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                    <Check className="h-4 w-4 mr-2" />
                                )}
                                Guardar
                            </Button>
                            {isConfigured && (
                                <>
                                    <Button 
                                        variant="outline" 
                                        onClick={handleTest}
                                        disabled={testing}
                                    >
                                        {testing ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <RefreshCw className="h-4 w-4" />
                                        )}
                                    </Button>
                                    <Button 
                                        variant="outline" 
                                        onClick={() => setShowDeleteDialog(true)}
                                        className="text-red-500"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Sincronización */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <RefreshCw className="h-5 w-5" />
                            Sincronización
                        </CardTitle>
                        <CardDescription>
                            {config?.last_sync 
                                ? `Última sincronización: ${new Date(config.last_sync).toLocaleString('es-CL')}`
                                : 'Nunca sincronizado'}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {!isConfigured ? (
                            <div className="text-center py-8 text-muted-foreground">
                                <AlertCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                                <p>Configura las credenciales primero</p>
                            </div>
                        ) : (
                            <>
                                <div className="grid grid-cols-2 gap-3">
                                    <Button 
                                        variant="outline" 
                                        onClick={handleSyncImport}
                                        disabled={syncing}
                                        className="h-auto py-4"
                                    >
                                        <div className="flex flex-col items-center">
                                            <Download className="h-6 w-6 mb-2" />
                                            <span>Importar</span>
                                            <span className="text-xs text-muted-foreground">4sat → Sistema</span>
                                        </div>
                                    </Button>
                                    <Button 
                                        variant="outline" 
                                        onClick={handleSyncExport}
                                        disabled={syncing}
                                        className="h-auto py-4"
                                    >
                                        <div className="flex flex-col items-center">
                                            <Upload className="h-6 w-6 mb-2" />
                                            <span>Exportar</span>
                                            <span className="text-xs text-muted-foreground">Sistema → 4sat</span>
                                        </div>
                                    </Button>
                                </div>

                                <Button 
                                    onClick={handleSyncFull}
                                    disabled={syncing}
                                    className="w-full"
                                    data-testid="sync-full-btn"
                                >
                                    {syncing ? (
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                        <RefreshCw className="h-4 w-4 mr-2" />
                                    )}
                                    Sincronización Completa
                                </Button>

                                <Button 
                                    variant="outline" 
                                    onClick={handleViewDevices}
                                    className="w-full"
                                >
                                    <Car className="h-4 w-4 mr-2" />
                                    Ver dispositivos en 4sat.cl
                                </Button>

                                {/* Botón Captura Excel */}
                                <Button 
                                    onClick={handleCapturaExcel}
                                    disabled={downloading || importing}
                                    className="w-full bg-green-600 hover:bg-green-700"
                                    data-testid="captura-excel-btn"
                                >
                                    {downloading ? (
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                        <Download className="h-4 w-4 mr-2" />
                                    )}
                                    Captura 4sat (Excel)
                                </Button>

                                {/* Botón Importación Inteligente */}
                                <Button 
                                    onClick={handleImportacionInteligente}
                                    disabled={importing || downloading}
                                    className="w-full bg-purple-600 hover:bg-purple-700"
                                    data-testid="importacion-inteligente-btn"
                                >
                                    {importing ? (
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                        <RefreshCw className="h-4 w-4 mr-2" />
                                    )}
                                    Importación Inteligente
                                </Button>

                                {/* Resultado de importación inteligente */}
                                {importResult && (
                                    <div className="mt-4 p-4 bg-purple-50 dark:bg-purple-950 rounded-lg border border-purple-200 dark:border-purple-800">
                                        <h4 className="font-medium mb-3 text-purple-800 dark:text-purple-200">Resultado Importación:</h4>
                                        <div className="grid grid-cols-2 gap-3 text-sm">
                                            <div className="text-center p-2 bg-white dark:bg-gray-800 rounded">
                                                <div className="text-xl font-bold text-green-600">{importResult.clientes_creados}</div>
                                                <div className="text-muted-foreground text-xs">Clientes nuevos</div>
                                            </div>
                                            <div className="text-center p-2 bg-white dark:bg-gray-800 rounded">
                                                <div className="text-xl font-bold text-blue-600">{importResult.clientes_existentes}</div>
                                                <div className="text-muted-foreground text-xs">Clientes existentes</div>
                                            </div>
                                            <div className="text-center p-2 bg-white dark:bg-gray-800 rounded">
                                                <div className="text-xl font-bold text-green-600">{importResult.vehiculos_creados}</div>
                                                <div className="text-muted-foreground text-xs">Vehículos nuevos</div>
                                            </div>
                                            <div className="text-center p-2 bg-white dark:bg-gray-800 rounded">
                                                <div className="text-xl font-bold text-blue-600">{importResult.vehiculos_actualizados}</div>
                                                <div className="text-muted-foreground text-xs">Vehículos actualizados</div>
                                            </div>
                                            <div className="text-center p-2 bg-white dark:bg-gray-800 rounded">
                                                <div className="text-xl font-bold text-purple-600">{importResult.dispositivos_instalados}</div>
                                                <div className="text-muted-foreground text-xs">Dispositivos instalados</div>
                                            </div>
                                            <div className="text-center p-2 bg-white dark:bg-gray-800 rounded">
                                                <div className="text-xl font-bold text-amber-600">{importResult.dispositivos_stock}</div>
                                                <div className="text-muted-foreground text-xs">A stock disponible</div>
                                            </div>
                                        </div>
                                        {importResult.errores?.length > 0 && (
                                            <div className="mt-3 text-sm text-red-600">
                                                {importResult.errores.length} error(es) durante la importación
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Resultado de sincronización */}
                                {syncResult && (
                                    <div className="mt-4 p-4 bg-muted rounded-lg">
                                        <h4 className="font-medium mb-2">Resultado:</h4>
                                        <div className="grid grid-cols-3 gap-2 text-sm">
                                            <div className="text-center">
                                                <div className="text-2xl font-bold text-green-600">{syncResult.imported}</div>
                                                <div className="text-muted-foreground">Importados</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-2xl font-bold text-blue-600">{syncResult.updated}</div>
                                                <div className="text-muted-foreground">Actualizados</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-2xl font-bold text-purple-600">{syncResult.exported}</div>
                                                <div className="text-muted-foreground">Exportados</div>
                                            </div>
                                        </div>
                                        {syncResult.errors?.length > 0 && (
                                            <div className="mt-3 text-sm text-red-600">
                                                {syncResult.errors.length} error(es)
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Info de campos */}
            <Card>
                <CardHeader>
                    <CardTitle>Campos sincronizados</CardTitle>
                    <CardDescription>
                        Los siguientes campos se sincronizan entre sistemas
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                        <div>
                            <h4 className="font-medium mb-2">Vehículo</h4>
                            <ul className="space-y-1 text-muted-foreground">
                                <li>• Patente</li>
                                <li>• Marca</li>
                                <li>• Modelo</li>
                                <li>• Año</li>
                                <li>• Tipo</li>
                                <li>• Chasis/VIN</li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-medium mb-2">Dispositivo GPS</h4>
                            <ul className="space-y-1 text-muted-foreground">
                                <li>• Marca GPS</li>
                                <li>• Modelo GPS</li>
                                <li>• IMEI</li>
                                <li>• Frecuencia transmisión</li>
                                <li>• Fecha instalación</li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-medium mb-2">Empresa</h4>
                            <ul className="space-y-1 text-muted-foreground">
                                <li>• Razón Social</li>
                                <li>• RUT</li>
                            </ul>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Dialog de dispositivos */}
            <Dialog open={showDevices} onOpenChange={setShowDevices}>
                <DialogContent className="max-w-4xl max-h-[80vh] overflow-auto">
                    <DialogHeader>
                        <DialogTitle>Dispositivos en 4sat.cl</DialogTitle>
                        <DialogDescription>
                            {devices.length} dispositivos encontrados
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        {devices.map((device, idx) => (
                            <div key={idx} className="p-3 border rounded-lg text-sm">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <span className="font-medium">{device.qr_plate || device.name}</span>
                                        <span className="text-muted-foreground ml-2">ID: {device.id}</span>
                                    </div>
                                    <Badge variant="outline">{device.qr_device_type || 'Sin tipo'}</Badge>
                                </div>
                                <div className="grid grid-cols-2 gap-2 mt-2 text-muted-foreground">
                                    <div>Marca: {device.qr_device_branding || '-'}</div>
                                    <div>Modelo: {device.qr_device_model || '-'}</div>
                                    <div>GPS: {device.qr_branding || '-'}</div>
                                    <div>IMEI: {device.imei || '-'}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Dialog de eliminar */}
            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Eliminar configuración</DialogTitle>
                        <DialogDescription>
                            ¿Estás seguro de eliminar la configuración de 4sat.cl? 
                            Los vehículos ya importados se mantendrán.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                            Cancelar
                        </Button>
                        <Button variant="destructive" onClick={handleDelete}>
                            Eliminar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
